import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getMetaPricingMessage,
  META_PRICING_ALERT_IDS,
} from 'src/alerts/MetaPricingNotice.types';
import { RouterPaths } from 'src/components/Router/Router.constants';
import {
  ALERT_CENTER_EVENTS,
  type tAlertDetailOpenedProps,
  type tMetaPricingEventProps,
} from 'src/hooks/analytics/alertCenterAnalytics.constants';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useAlertCenterContext } from 'src/providers/AlertCenterProvider/AlertCenter.hook';
import { isCreditsToBalanceNoticeClosed } from 'src/pages/Dashboard/components/CreditsToBalanceNotice/CreditsToBalanceNotice.storage';
import AlertDetailModal from './AlertDetailModal.component';
import { useAlertDetailHost } from './AlertDetailHost.hook';
import {
  isMetaPricingNoticeDismissedForever,
  isMetaPricingNoticeSeenThisSession,
  markMetaPricingNoticeSeenThisSession,
} from './MetaPricingNotice.storage';

export type tAutoOpenNoticeHostProps = {
  /** Both ids are REQUIRED: see the mount contract below. */
  workspaceId: string;
  userId: string;
};

/**
 * AGI-428: the only thing in the app that opens an alert detail by itself.
 *
 * It opens the EXISTING detail (`useAlertDetailHost` + `AlertDetailModal`), not
 * a modal of its own, so the automatic version and the one the user reaches
 * from the bell are literally the same component. Two modals showing the same
 * notice is how one of them ends up a version behind.
 *
 * 🔴 MOUNT CONTRACT. The parent mounts this only once BOTH ids are defined and
 * with `key={workspaceId + userId}`
 * (docs/solutions/logic-errors/late-entity-id-lazy-init-clobbers-persisted-
 * state.md). The storage marks are scoped by the pair, so a component that
 * mounts with an undefined id would read the wrong bucket, decide there is no
 * mark, and open a modal the user already switched off, for the rest of that
 * session. The ids are props rather than context reads for the same reason:
 * the gate belongs to whoever can enforce the `key`.
 *
 * 🔴 EVENTS NEED A WORKSPACE. `useAnalytics().trackEvent` drops anything
 * emitted without `currentWorkspaceId`
 * (docs/solutions/logic-errors/analytics-events-before-workspace-context-are-
 * dropped.md). The mount gate above is what guarantees this host never runs
 * before that, so `detail.opened` with `source: 'auto'` is actually recorded
 * rather than silently swallowed.
 *
 * What it does NOT do:
 *
 *  - it never writes on close. "Cerrar" and Escape leave no trace beyond the
 *    session mark, so the next session opens the notice again, which is the
 *    product decision (only the checkbox is permanent).
 *  - it never acknowledges. Only the bell's "Marcar como visto" does, and an
 *    acknowledged notice never opens by itself again (see `alerts` below).
 *  - it never re-opens the same family in the same mount. The ref below is
 *    the synchronous guard; the session mark is the one that survives a
 *    remount.
 */
export default function AutoOpenNoticeHost(props: tAutoOpenNoticeHostProps) {
  const { workspaceId, userId } = props;
  const { alerts, metaPricingNotice } = useAlertCenterContext();
  const { trackEvent } = useAnalytics();
  const detailHost = useAlertDetailHost();
  const { pathname } = useLocation();
  const isOnDashboard = pathname.startsWith(RouterPaths.dashboard);
  /*
   * The FAMILY this mount already opened, not a boolean: when message 2 goes
   * live under a tab that auto-opened message 1 earlier, the new message
   * still gets its one automatic opening (on the next focus refetch, the same
   * way message 1's first batch reached tabs that were already open).
   */
  const autoOpenedIdRef = useRef<string | null>(null);

  /*
   * `alerts`, the list WITHOUT acknowledgments, not `derivedAlerts`. Since
   * Ariel's 16-sep call the family is dismissible (it counts in the bell
   * number), and someone who pressed "Marcar como visto" has told us they read
   * it: opening the same notice at them on the next login would be the nag the
   * acknowledgment exists to stop. Both lists come out of the same selection,
   * so nothing else changes. The acknowledgment is keyed `<alert>:<workspace>`
   * and survives a failed refetch (`acknowledgmentOutlivesEpisode`), so it
   * cannot silently wear off and bring the modal back.
   *
   * The two marks below still do their own jobs: the checkbox turns off only
   * the automatic opening (the row stays in the bell), and the session mark
   * keeps a reload from reopening it.
   */
  /*
   * Either message (AGI-428, message 2 on 25-sep). The source serves one at a
   * time, so at most one family is live; the marks below are scoped by the
   * message of the notice actually on screen.
   */
  const liveAlert =
    alerts.find((alert) => META_PRICING_ALERT_IDS.includes(alert.id)) ?? null;
  const message = metaPricingNotice
    ? getMetaPricingMessage(metaPricingNotice.key)
    : undefined;

  // Refs so the effect below depends on the ALERT becoming live, not on a new
  // `trackEvent` identity or a fresh payload object from a focus refetch.
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const payloadRef = useRef(metaPricingNotice?.payload ?? null);
  payloadRef.current = metaPricingNotice?.payload ?? null;
  const openRef = useRef(detailHost.open);
  openRef.current = detailHost.open;

  useEffect(() => {
    if (!liveAlert) return;
    if (autoOpenedIdRef.current === liveAlert.id) return;
    /*
     * Belt and braces over the parent's mount gate. Without an id the storage
     * reads answer "no mark" for EVERY user, so a host that ran here would
     * reopen a notice somebody already switched off, and `trackEvent` would
     * drop the event anyway (no workspace context). Cheap, and it makes the
     * precondition testable without reconstructing the provider tree.
     */
    if (!workspaceId || !userId) return;

    const scope = { workspaceId, userId, message };
    if (isMetaPricingNoticeDismissedForever(scope)) return;
    if (isMetaPricingNoticeSeenThisSession(scope)) return;
    /*
     * Message 3 waits for the credits-to-balance notice (RET-2142) where the
     * two can meet. That one opens by itself, once, on the Dashboard, for the
     * same workspaces (both gate on `useWalletSurfaceAccess`), and two
     * automatic modals stacked on the first screen after login is the one
     * thing neither of them can afford. So on the Dashboard, while it has not
     * been closed, message 3 stays in the bell; the next page or the next
     * session opens it. Nothing is marked here, so the wait costs nothing.
     */
    if (
      message === 'balance' &&
      isOnDashboard &&
      !isCreditsToBalanceNoticeClosed({ workspaceId, userId })
    ) {
      return;
    }

    // Flipped BEFORE the open so a re-render inside the same tick cannot fire
    // twice, and written to storage in the same breath so a remount in this
    // session (a route change that rebuilds the tree) does not reopen it.
    autoOpenedIdRef.current = liveAlert.id;
    markMetaPricingNoticeSeenThisSession(scope);
    // No invoker: nothing was clicked, so there is no element to return focus
    // to. `useAlertDetailHost` falls through to its no-op fallback.
    openRef.current(liveAlert, null);

    const payload = payloadRef.current;
    const noticeProps: tMetaPricingEventProps | Record<string, never> = payload
      ? {
          segment: payload.segment,
          urgency: payload.urgency,
          key_account: payload.keyAccount,
          cta_url: payload.ctaUrl,
        }
      : {};
    const openedProps: tAlertDetailOpenedProps = {
      alert_id: liveAlert.id,
      from: 'auto',
      source: 'auto',
      ...noticeProps,
    };
    trackEventRef.current(ALERT_CENTER_EVENTS.DETAIL_OPENED, openedProps);
  }, [liveAlert, workspaceId, userId, message, isOnDashboard]);

  if (!detailHost.alert) return null;

  /*
   * MOUNTED conditionally, never hidden (KTD14). The modal stays up even when
   * the notice resolves underneath it (the 5-oct expiry landing on an open
   * dialog): `AlertDetailModal` swaps the body for its resolved note and the
   * user closes it themselves, because a modal that vanishes mid-read looks
   * like a crash rather than a resolution.
   */
  return (
    <AlertDetailModal alert={detailHost.alert} onClose={detailHost.close} />
  );
}
